import { useEffect, useState } from 'react';
import type { BuildHistory, Idea } from '../api';
import * as api from '../api';

// "Build history" in the idea panel (P5-09, Pro): commits and PRs from
// connected repos that mention the idea or are linked to it, a 12-week
// sparkline, and how busy its repos have been. Hidden when there's nothing.

const SHOWN = 8;

function when(sql: string): string {
  return new Date(sql.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function Sparkline({ weeks }: { weeks: BuildHistory['weeks'] }) {
  const W = 120;
  const H = 24;
  const max = Math.max(1, ...weeks.map((w) => w.commits + w.prs_merged));
  const step = W / weeks.length;
  const total = weeks.reduce((n, w) => n + w.commits + w.prs_merged, 0);
  return (
    <svg
      className="build-spark"
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`${plural(total, 'commit or merge')} in the last ${weeks.length} weeks`}
    >
      {weeks.map((w, i) => {
        const n = w.commits + w.prs_merged;
        const h = n > 0 ? Math.max(2, (n / max) * (H - 2)) : 1;
        return (
          <rect key={w.start} x={i * step + 1} y={H - h} width={Math.max(1, step - 2)} height={h} rx={1} className={n > 0 ? 'on' : ''}>
            <title>{`Week of ${w.start}: ${plural(w.commits, 'commit')}${w.prs_merged ? `, ${plural(w.prs_merged, 'PR')} merged` : ''}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

export function BuildHistorySection({ idea, isPro }: { idea: Idea; isPro: boolean }) {
  const [history, setHistory] = useState<BuildHistory | null>(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    setHistory(null);
    setAll(false);
    if (!isPro) return;
    let live = true;
    api.getIdeaBuildHistory(idea.id).then((r) => { if (live) setHistory(r.history); }).catch(() => {});
    return () => { live = false; };
  }, [idea.id, isPro]);

  if (!history || (history.events.length === 0 && history.repos.length === 0)) return null;
  const { events, totals, repos } = history;
  const shown = all ? events : events.slice(0, SHOWN);

  return (
    <div className="detail-section">
      <div className="detail-label">BUILD HISTORY</div>
      <div className="build-history-head">
        <Sparkline weeks={history.weeks} />
        <span className="build-list-meta">
          {plural(totals.commits, 'commit')} · {plural(totals.prs, 'PR')}
          {totals.prs_merged > 0 && ` (${totals.prs_merged} merged)`}
        </span>
      </div>
      {repos.length > 0 && (
        <p className="build-list-meta">
          {repos.map((r) => `${r.repo}: ${plural(r.commits, 'commit')} in 30 days`).join(' · ')}
        </p>
      )}
      {shown.length > 0 && (
        <ul className="build-history-list">
          {shown.map((e) => (
            <li key={`${e.kind}:${e.repo}:${e.ref}`}>
              <span className={`build-history-kind mono${e.state === 'merged' ? ' merged' : ''}`}>
                {e.kind === 'commit' ? e.ref.slice(0, 7) : `#${e.ref}`}
              </span>
              <span className="build-history-title">
                {e.url ? <a href={e.url} target="_blank" rel="noopener noreferrer">{e.title ?? e.ref}</a> : (e.title ?? e.ref)}
              </span>
              <span className="build-list-meta">
                {e.repo} · {e.kind === 'pull_request' && e.state ? `${e.state} · ` : ''}{when(e.merged_at ?? e.occurred_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {events.length > SHOWN && (
        <button className="btn-ghost-sm" onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `Show all ${events.length}`}
        </button>
      )}
    </div>
  );
}
